import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useMccCodes } from '../contexts/MccContext';

const Combobox = ({ options, value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = query === ''
    ? options
    : options.filter((option) =>
        option.toLowerCase().includes(query.toLowerCase())
      );

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
             onChange(event.target.value);
             setQuery(event.target.value);
             setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <FontAwesomeIcon icon={faLayerGroup} className="text-gray-400 w-3 h-3" />
        </div>
      </div>
      
      {isOpen && filteredOptions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-xl py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {filteredOptions.map((option) => (
            <li
              key={option}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-900 dark:text-white"
              onClick={() => {
                onChange(option);
                setQuery('');
                setIsOpen(false);
              }}
            >
              <span className={`block truncate ${value === option ? 'font-bold' : 'font-normal'}`}>
                {option}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MccEditModal = ({ isOpen, onClose, initialData, zIndex = 50, backdropClass = "bg-black/50" }) => {
    const { t } = useTranslation();
    const { categories, refreshCodes } = useMccCodes();
    
    const [formData, setFormData] = useState({ code: '', description: '', category: '' });
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && initialData) {
            setFormData({
                code: initialData.code || '',
                description: initialData.description || '',
                category: initialData.category || ''
            });
        }
    }, [isOpen, initialData]);

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (initialData && initialData._id) {
                await axios.put(`/mcc-codes/${initialData._id}`, formData);
            } else {
                await axios.post('/mcc-codes', formData);
            }
            refreshCodes();
            onClose();
        } catch (error) {
            setError(error.response?.data?.error || "Error saving code");
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className={`fixed inset-0 flex items-center justify-center p-4 backdrop-blur-sm ${backdropClass}`} style={{ zIndex: zIndex }}>
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="bg-white dark:bg-dark-surface w-full md:max-w-md rounded-2xl shadow-xl"
                    >
                        <form onSubmit={handleSave}>
                            <div className="p-6 space-y-4">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                    {initialData && initialData._id ? t('editMccCode', 'Редагувати MCC Код') : t('addMccCode', 'Додати MCC Код')}
                                </h3>
                                
                                {error && (
                                    <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm mb-4 border border-red-100">
                                        {error}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('code', 'Код')}</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={formData.code}
                                        onChange={e => setFormData({...formData, code: e.target.value})}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('description', 'Опис')}</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={formData.description}
                                        onChange={e => setFormData({...formData, description: e.target.value})}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('category', 'Категорія')}</label>
                                    <Combobox 
                                        options={categories.map(c => c.name)}
                                        value={formData.category}
                                        onChange={(val) => setFormData({...formData, category: val})}
                                        placeholder={t('selectCategory', 'Виберіть категорію')}
                                    />
                                </div>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2 rounded-b-2xl">
                                <button 
                                    type="button" 
                                    onClick={onClose}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-xl transition-colors"
                                >
                                    {t('cancel', 'Скасувати')}
                                </button>
                                <button 
                                    type="submit"
                                    className="btn-primary px-6 py-2 rounded-xl"
                                >
                                    {t('save', 'Зберегти')}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default MccEditModal;
